import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Filter, TrendingUp, ArrowUp, ArrowDown, ArrowUpDown, Plus, Search, Trash2, Camera } from "lucide-react";
import Navbar from "../components/Navbar";
import DailyPicksWidget from "../components/DailyPicksWidget";
import TickerSearchInput from "../components/TickerSearchInput";
import { api } from "../lib/api";
import {
  fmtMoney,
  fmtPct,
  verdictStyle,
  marginColor,
  marketFromTicker,
  displayTicker,
  MARKETS,
  normalizeTicker,
} from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const VERDICT_FILTERS = ["ALL", "UNDERVALUED", "HOLD", "OVERVALUED"];
const SECTOR_FILTERS = ["ALL"];

export default function Demo() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verdictFilter, setVerdictFilter] = useState("ALL");
  const [marketFilter, setMarketFilter] = useState("ALL");
  const [sectorFilter, setSectorFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [cached, setCached] = useState(false);
  const [ageSec, setAgeSec] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [sortBy, setSortBy] = useState("margin_of_safety_percent");
  const [sortDir, setSortDir] = useState("desc"); // 'desc' = highest first
  const [tickerInput, setTickerInput] = useState("");
  const [market, setMarket] = useState("NSE");
  const [adding, setAdding] = useState(false);
  const [scanning, setScanning] = useState(false);
  const cameraRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const { data } = await api.get("/universe", { timeout: 120000 });
      setRows(data.results || []);
      setCached(true);
      setAgeSec(null);
      if (!data.results || data.results.length === 0) {
        setErrorMsg("My Universe is empty. Add a ticker above to get started.");
      }
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.detail ||
          err?.message ||
          "Couldn't load your universe. The server may be warming up."
      );
      toast.error("Failed to load My Universe");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e, overrideSymbol) => {
    e?.preventDefault?.();
    const rawInput = (overrideSymbol || tickerInput || "").trim();
    if (!rawInput || adding) return;
    // If the symbol looks like an NSE ticker already, keep it; else pass through normalizeTicker
    const t = /[.]/.test(rawInput) ? rawInput.toUpperCase() : normalizeTicker(rawInput, market);
    if (!t) return;
    setAdding(true);
    try {
      await api.post("/universe", { ticker: t });
      toast.success(`${displayTicker(t)} added to your universe`);
      setTickerInput("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add stock");
    } finally {
      setAdding(false);
    }
  };

  const handlePhotoScan = async (e) => {
    const f = e.target.files?.[0];
    if (!f || scanning) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: r } = await api.post("/universe/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(`${r.count} stock${r.count === 1 ? "" : "s"} added from photo`);
      if ((r.unmatched || []).length > 0) {
        toast.warning(`Couldn't match: ${r.unmatched.slice(0, 3).join(", ")}${r.unmatched.length > 3 ? "…" : ""}`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo scan failed");
    } finally {
      setScanning(false);
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const handleRemove = async (e, ticker) => {
    e?.stopPropagation();
    try {
      await api.delete(`/universe/${encodeURIComponent(ticker)}`);
      setRows((r) => r.filter((x) => x.ticker !== ticker));
      toast.success(`${displayTicker(ticker)} removed from your universe`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove stock");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sectors = useMemo(() => {
    const s = new Set(["ALL"]);
    rows.forEach((r) => r.sector && s.add(r.sector));
    return Array.from(s);
  }, [rows]);

  const filtered = useMemo(() => {
    const matched = rows.filter((r) => {
      if (verdictFilter !== "ALL" && r.verdict !== verdictFilter) return false;
      if (marketFilter !== "ALL" && marketFromTicker(r.ticker) !== marketFilter) return false;
      if (sectorFilter !== "ALL" && r.sector !== sectorFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !r.ticker.toLowerCase().includes(q) &&
          !(r.company_name || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });

    // Sort
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...matched].sort((a, b) => {
      const va = a[sortBy];
      const vb = b[sortBy];
      // Push nulls/undefined to the end regardless of direction
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      if (typeof va === "string") return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return sorted;
  }, [rows, verdictFilter, marketFilter, sectorFilter, search, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      // Default to descending for "bigger is better" metrics, ascending for ratios
      setSortDir(["pe_ratio", "forward_pe"].includes(key) ? "asc" : "desc");
    }
  };

  const counts = useMemo(() => {
    const c = { UNDERVALUED: 0, HOLD: 0, OVERVALUED: 0 };
    rows.forEach((r) => {
      if (c[r.verdict] !== undefined) c[r.verdict] += 1;
    });
    return c;
  }, [rows]);

  return (
    <div className="min-h-screen bg-bone-50" data-testid="demo-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="mb-10">
          <div className="overline mb-3">— Your Research Worksheet</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone_brand-dark tracking-tight font-semibold">
            My Universe
          </h1>
          <p className="text-stone_brand mt-2 max-w-2xl">
            Your personal stock list with <span className="font-mono text-moss">Morningstar fair values</span> and
            <span className="font-mono text-moss"> 2030 targets</span> loaded — cross-checked against live yfinance data.
            Click any row for full AI digest. Sort any column; filter by market, verdict, or sector.
          </p>
        </div>

        {/* Daily Picks widget — top of page */}
        <DailyPicksWidget scope="universe" limit={6} />

        {/* Add ticker form */}
        <form
          onSubmit={handleAdd}
          className="bg-white border border-line rounded-sm p-5 mb-8 flex flex-col gap-3"
          data-testid="universe-add-form"
        >
          <div className="flex items-center gap-1.5" data-testid="universe-market-selector">
            {MARKETS.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMarket(m.key)}
                className={`px-3 py-1 rounded-sm text-xs font-mono tracking-wider transition-colors ${
                  market === m.key
                    ? "bg-moss text-bone-50"
                    : "bg-white border border-line text-stone_brand hover:border-moss/50"
                }`}
                data-testid={`universe-market-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[240px] max-w-md">
              <TickerSearchInput
                value={tickerInput}
                onChange={setTickerInput}
                onPick={(match) => {
                  // Auto-add on pick
                  setTickerInput(match.symbol);
                  handleAdd({ preventDefault: () => {} }, match.symbol);
                }}
                placeholder="Type a company (e.g. HDFC Bank, Reliance) or ticker"
                dataTestidBase="universe-ticker"
              />
            </div>
            <Button
              type="submit"
              disabled={adding || !tickerInput.trim()}
              className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-11 px-5 gap-1.5"
              data-testid="universe-add-btn"
            >
              <Plus className="w-4 h-4" /> Add to My Universe
            </Button>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhotoScan}
              data-testid="universe-camera-input"
            />
            <Button
              type="button"
              onClick={() => cameraRef.current?.click()}
              disabled={scanning}
              variant="outline"
              className="rounded-sm border-moss text-moss hover:bg-moss-bg h-11 px-4 gap-1.5"
              data-testid="universe-scan-photo-btn"
            >
              <Camera className="w-4 h-4" /> {scanning ? "Reading…" : "Scan Photo"}
            </Button>
          </div>
          <p className="text-[0.7rem] font-mono uppercase tracking-widest text-stone_brand-light">
            — Removed tickers are hidden from your list · hover any row to delete · auto-refreshes every 10 min
          </p>
        </form>

        {/* Summary bar */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <SummaryTile
            label="Undervalued"
            value={counts.UNDERVALUED}
            accent="text-moss-light"
            bg="bg-moss-bg border-moss-light/30"
            onClick={() => setVerdictFilter("UNDERVALUED")}
            active={verdictFilter === "UNDERVALUED"}
            testid="tile-undervalued"
          />
          <SummaryTile
            label="Hold / Fair"
            value={counts.HOLD}
            accent="text-amber_brand"
            bg="bg-amber_brand-bg border-amber_brand/30"
            onClick={() => setVerdictFilter("HOLD")}
            active={verdictFilter === "HOLD"}
            testid="tile-hold"
          />
          <SummaryTile
            label="Overvalued"
            value={counts.OVERVALUED}
            accent="text-terracotta"
            bg="bg-terracotta-bg border-terracotta/30"
            onClick={() => setVerdictFilter("OVERVALUED")}
            active={verdictFilter === "OVERVALUED"}
            testid="tile-overvalued"
          />
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-line rounded-sm p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-stone_brand-light" />
            <span className="overline">Filters</span>
          </div>

          {/* Market */}
          <div className="flex items-center gap-1">
            {["ALL", "US", "NSE"].map((m) => (
              <button
                key={m}
                onClick={() => setMarketFilter(m)}
                className={`px-3 py-1 rounded-sm text-xs font-mono tracking-wider transition-colors ${
                  marketFilter === m
                    ? "bg-moss text-bone-50"
                    : "bg-white border border-line text-stone_brand hover:border-moss/50"
                }`}
                data-testid={`demo-filter-market-${m}`}
              >
                {m}
              </button>
            ))}
          </div>

          <div className="h-5 w-px bg-line" />

          {/* Verdict */}
          <div className="flex items-center gap-1">
            {VERDICT_FILTERS.map((v) => (
              <button
                key={v}
                onClick={() => setVerdictFilter(v)}
                className={`px-3 py-1 rounded-sm text-xs font-mono tracking-wider transition-colors ${
                  verdictFilter === v
                    ? "bg-moss text-bone-50"
                    : "bg-white border border-line text-stone_brand hover:border-moss/50"
                }`}
                data-testid={`demo-filter-verdict-${v}`}
              >
                {v}
              </button>
            ))}
          </div>

          <div className="h-5 w-px bg-line" />

          {/* Sector */}
          <select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            className="border border-line bg-white rounded-sm px-3 py-1 text-xs font-mono tracking-wider text-stone_brand focus:ring-2 focus:ring-moss outline-none"
            data-testid="demo-filter-sector"
          >
            {sectors.map((s) => (
              <option key={s} value={s}>
                {s === "ALL" ? "ALL SECTORS" : s.toUpperCase()}
              </option>
            ))}
          </select>

          <div className="flex-1" />

          {/* Quick sort flip for margin of safety */}
          <button
            onClick={() => {
              setSortBy("margin_of_safety_percent");
              setSortDir((d) => (sortBy === "margin_of_safety_percent" && d === "desc" ? "asc" : "desc"));
            }}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-mono tracking-wider transition-colors ${
              sortBy === "margin_of_safety_percent"
                ? "bg-moss text-bone-50"
                : "bg-white border border-line text-stone_brand hover:border-moss/50"
            }`}
            data-testid="sort-margin-toggle"
            title="Toggle Margin of Safety sort direction"
          >
            MoS
            {sortBy === "margin_of_safety_percent" ? (
              sortDir === "desc" ? (
                <ArrowDown className="w-3 h-3" />
              ) : (
                <ArrowUp className="w-3 h-3" />
              )
            ) : (
              <ArrowUpDown className="w-3 h-3" />
            )}
          </button>

          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ticker / company…"
            className="max-w-xs rounded-sm border-line font-mono h-9 focus-visible:ring-moss"
            data-testid="demo-search-input"
          />
          <span className="overline text-stone_brand-light">
            {filtered.length} / {rows.length}
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white border border-line rounded-sm p-12 text-center">
            <TrendingUp className="w-8 h-8 text-moss animate-pulse mx-auto mb-4" />
            <div className="font-serif-display text-xl text-stone_brand-dark mb-2">
              Fetching 100 stocks…
            </div>
            <p className="text-sm text-stone_brand">
              Live data from Yahoo Finance — may take up to a minute on cold start, then cached.
            </p>
          </div>
        ) : errorMsg || rows.length === 0 ? (
          <div className="bg-white border border-line rounded-sm p-12 text-center" data-testid="demo-empty-state">
            <div className="font-serif-display text-xl text-stone_brand-dark mb-2">
              {rows.length === 0 ? "No stocks loaded yet" : "No matches"}
            </div>
            <p className="text-sm text-stone_brand mb-5">
              {errorMsg ||
                "The server is warming up the 100-stock cache. This takes ~15-20 seconds on first run."}
            </p>
            <Button
              onClick={load}
              className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50"
              data-testid="demo-retry-btn"
            >
              Retry
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-line rounded-sm p-12 text-center">
            <div className="font-serif-display text-xl text-stone_brand-dark mb-2">
              No matches
            </div>
            <p className="text-sm text-stone_brand">Try loosening your filters.</p>
          </div>
        ) : (
          <div className="bg-white border border-line rounded-sm overflow-x-auto" data-testid="demo-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line text-left sticky top-0 bg-white">
                  <th className="overline py-4 px-3 text-stone_brand-light whitespace-nowrap">#</th>
                  <SortHeader label="Ticker"           col="ticker"                     sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Sector"           col="sector"                     sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Price"            col="current_price"              sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="M-Star FV"        col="user_morningstar_fv"        sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="M-Star MoS"       col="morningstar_margin_percent" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="Mean Target"      col="analyst_target_mean"        sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="Margin of Safety" col="margin_of_safety_percent"   sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric primary />
                  <SortHeader label="2030 Target"      col="user_target_2030"           sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <SortHeader label="2030 Upside"      col="upside_2030_percent"        sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} numeric />
                  <th className="overline py-4 px-3 text-stone_brand-light whitespace-nowrap">Verdict</th>
                  <th className="overline py-4 px-3 text-stone_brand-light whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => {
                  const cur = s.currency || "USD";
                  const mkt = marketFromTicker(s.ticker);
                  return (
                    <tr
                      key={s.ticker}
                      onClick={() => navigate(`/stock/${encodeURIComponent(s.ticker)}`)}
                      className="border-b border-line last:border-0 hover:bg-bone-100 cursor-pointer transition-colors"
                      data-testid={`demo-row-${s.ticker}`}
                    >
                      <td className="py-3 px-3 font-mono text-xs text-stone_brand-light">
                        {String(i + 1).padStart(3, "0")}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-stone_brand-dark">
                            {displayTicker(s.ticker)}
                          </span>
                          <span className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1 rounded-sm">
                            {mkt}
                          </span>
                        </div>
                        <div className="text-xs text-stone_brand truncate max-w-[180px]">
                          {s.company_name}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-xs text-stone_brand whitespace-nowrap">
                        {s.sector || "—"}
                      </td>
                      <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                        {fmtMoney(s.current_price, cur)}
                      </td>
                      <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                        {fmtMoney(s.user_morningstar_fv, cur)}
                      </td>
                      <td className={`py-3 px-3 font-mono text-sm ${marginColor(s.morningstar_margin_percent)}`}>
                        {fmtPct(s.morningstar_margin_percent)}
                      </td>
                      <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                        {fmtMoney(s.analyst_target_mean, cur)}
                      </td>
                      <td className={`py-3 px-3 font-mono text-sm font-semibold ${marginColor(s.margin_of_safety_percent)}`}>
                        {fmtPct(s.margin_of_safety_percent)}
                      </td>
                      <td className="py-3 px-3 font-mono text-sm text-stone_brand-dark">
                        <div>{fmtMoney(s.user_target_2030, cur)}</div>
                        {s.user_multiplier_2030 && (
                          <div className="text-[0.65rem] text-stone_brand-light">
                            {s.user_multiplier_2030}
                          </div>
                        )}
                      </td>
                      <td className={`py-3 px-3 font-mono text-sm font-semibold ${marginColor(s.upside_2030_percent)}`}>
                        {fmtPct(s.upside_2030_percent)}
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`px-2 py-0.5 rounded-sm text-[0.65rem] font-mono font-medium border ${verdictStyle(s.verdict)}`}
                        >
                          {s.verdict}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <button
                          onClick={(e) => handleRemove(e, s.ticker)}
                          className="p-1.5 text-stone_brand-light hover:text-terracotta hover:bg-terracotta-bg rounded-sm transition-colors"
                          data-testid={`universe-remove-${s.ticker}`}
                          aria-label={`Remove ${s.ticker}`}
                          title="Remove from My Universe"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="mt-4 text-xs text-stone_brand-light font-mono">
            {cached ? "Cached · " : "Live fetch · "}
            {rows.length} tickers loaded
            {ageSec !== null ? ` · refreshed ${ageSec}s ago` : ""} ·
            auto-refreshes every 10 minutes
          </div>
        )}
      </main>
    </div>
  );
}

function SummaryTile({ label, value, accent, bg, onClick, active, testid }) {
  return (
    <button
      onClick={onClick}
      className={`border rounded-sm p-5 text-left transition-all ${bg} ${
        active ? "ring-2 ring-offset-2 ring-moss" : "hover:opacity-90"
      }`}
      data-testid={testid}
    >
      <div className="overline mb-1.5">{label}</div>
      <div className={`font-mono text-3xl font-semibold ${accent}`}>{value}</div>
    </button>
  );
}

function SortHeader({ label, col, sortBy, sortDir, onClick, numeric, primary }) {
  const active = sortBy === col;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      onClick={() => onClick(col)}
      className={`overline py-4 px-4 whitespace-nowrap cursor-pointer select-none transition-colors group ${
        active ? "text-moss" : "text-stone_brand-light hover:text-stone_brand-dark"
      } ${primary ? "border-b-2 border-transparent" : ""}`}
      data-testid={`sort-${col}`}
    >
      <span className={`inline-flex items-center gap-1.5 ${numeric ? "" : ""}`}>
        {label}
        <Icon className={`w-3 h-3 transition-opacity ${active ? "opacity-100" : "opacity-30 group-hover:opacity-70"}`} />
      </span>
    </th>
  );
}
